// İSG uzmanı portalı — Araç belgesi ekleme (§23; Modal + RHF + Zod). PersonnelDocumentLinkModal ile
// aynı iki adımlı §8 akışı: (1) dosyayı yükle → confirmed `documentUuid`, (2) araca link'le.
// Link başarısız olursa yüklenen yetim §8 dosyası best-effort silinir. Başlangıç odağı GÖRÜNÜR
// "Dosya seç" düğmesindedir; gizli <input type=file> odaklanamaz (sr-only, tabIndex -1).
// Tür seçici ve bitiş tarihi kuralı §27 kaydının `vehicle` türlerinden (`documentLinkForm` +
// `DocumentTypeField`, GL #7): varsayılan tür yok; bitiş tarihi türün `expiryRule`'una göre
// zorunlu / isteğe bağlı / gizli.
//
// **KVKK/PII.** Dosya URL'i / documentUuid log'lanmaz; FileList reset'ten ÖNCE snapshot'lanır.

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  DOCUMENT_ACCEPT_ATTR,
  Field,
  Input,
  Modal,
  VEHICLE_NOTE_MAX,
  documentsApi,
  isTypeFieldRejection,
  resolveApiErrorMessage,
  useDocumentTypes,
  validateDocumentFile,
  vehiclesApi,
} from '@isgportal/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Paperclip } from 'lucide-react';
import { type RefObject, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { invalidateVehicleCaches } from '../../lib/vehicleCache';
import { DocumentTypeField } from '../documents/DocumentTypeField';
import { ValidUntilField } from '../documents/ValidUntilField';
import {
  type DocumentLinkFormValues,
  documentLinkSchema,
  useDocumentLinkType,
} from '../documents/documentLinkForm';

interface VehicleDocumentLinkModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contractorUuid: string;
  vehicleUuid: string;
  vehiclePlate: string;
  /** Açan denetimin ref'i — kapanışta odak buraya döner (menü öğesi DOM'dan kalkar). */
  restoreFocusRef?: RefObject<HTMLElement | null>;
}

function nullable(value: string | undefined): string | null {
  return value && value.trim().length > 0 ? value.trim() : null;
}

export function VehicleDocumentLinkModal({
  open,
  onOpenChange,
  contractorUuid,
  vehicleUuid,
  vehiclePlate,
  restoreFocusRef,
}: VehicleDocumentLinkModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const chooseButtonRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { types } = useDocumentTypes();
  const schema = useMemo(
    () =>
      documentLinkSchema(
        t,
        types,
        'vehicle',
        z
          .string()
          .trim()
          .max(VEHICLE_NOTE_MAX, t('vehicles.upload.noteTooLong', { max: VEHICLE_NOTE_MAX })),
      ),
    [t, types],
  );
  // Tür önceden seçilmez (belge yüklendikten sonra düzenlenemez): boş başlar, seçim zorunlu.
  const form = useForm<DocumentLinkFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { type: '', validFrom: '', validUntil: '', note: '' },
  });
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = form;
  const docType = useDocumentLinkType('vehicle', form);
  // Eşzamanlı çift Enter/tık: RHF doğrulaması async → senkron ref ikinci gönderimi keser.
  const inflightRef = useRef(false);

  function pickFile(files: File[]) {
    const picked = files[0];
    if (!picked) {
      return;
    }
    const invalid = validateDocumentFile(picked);
    if (invalid) {
      setFile(null);
      setFileError(t(invalid));
      return;
    }
    setFileError(null);
    setFile(picked);
  }

  function resetAll() {
    reset();
    setFile(null);
    setFileError(null);
    setProgress(null);
  }

  const mutation = useMutation({
    mutationFn: async (values: DocumentLinkFormValues) => {
      if (!file) {
        throw new Error('NO_FILE');
      }
      setProgress(0);
      const uploaded = await documentsApi.uploadDocument(file, { onProgress: setProgress });
      // Link KESİN reddedilirse (4xx) yetim §8 dosyası silinir; ağ hatası/5xx'te silinmez (bağ
      // kaydedilmiş olabilir) — `linkUploadedDocument`.
      return documentsApi.linkUploadedDocument(uploaded.uuid, () =>
        vehiclesApi.linkVehicleDocument(contractorUuid, vehicleUuid, {
          type: values.type,
          documentUuid: uploaded.uuid,
          validFrom: nullable(values.validFrom),
          validUntil: docType.validUntilToSend(values),
          note: nullable(values.note),
        }),
      );
    },
    onSuccess: () => {
      invalidateVehicleCaches(queryClient, contractorUuid);
      resetAll();
      onOpenChange(false);
    },
    // Tür reddi (§27.6): kayıt tazelenir, seçim temizlenir, hata alanda — aynı gövde yeniden gitmez.
    onError: (error) => docType.handleTypeRejection(error),
    onSettled: () => {
      inflightRef.current = false;
      setProgress(null);
    },
  });

  const uploading = mutation.isPending;

  function handleOpenChange(next: boolean) {
    if (uploading && !next) {
      return; // yükleme sürerken kapanmaz (yetim dosya/bayat durum riski)
    }
    if (!next) {
      mutation.reset();
      resetAll();
    }
    onOpenChange(next);
  }

  return (
    <Modal
      open={open}
      onOpenChange={handleOpenChange}
      restoreFocusRef={restoreFocusRef}
      title={t('vehicles.upload.title')}
      description={t('vehicles.upload.description', { plate: vehiclePlate })}
      size="md"
      closeLabel={t('contractors.common.close')}
      initialFocusRef={chooseButtonRef}
      footer={
        <>
          <Button variant="secondary" onClick={() => handleOpenChange(false)} disabled={uploading}>
            {t('contractors.common.cancel')}
          </Button>
          <Button
            type="submit"
            form="vehicle-document-form"
            loading={uploading}
            disabled={file === null || uploading}
          >
            {uploading ? t('vehicles.upload.uploading') : t('vehicles.upload.submit')}
          </Button>
        </>
      }
    >
      <form
        id="vehicle-document-form"
        className="flex flex-col gap-4"
        onSubmit={handleSubmit((values) => {
          if (inflightRef.current || mutation.isPending) {
            return; // gönderim sürüyor
          }
          inflightRef.current = true;
          mutation.mutate(values);
        })}
        noValidate
      >
        {mutation.isError && !isTypeFieldRejection(mutation.error) ? (
          <Alert tone="danger">{resolveApiErrorMessage(mutation.error, t)}</Alert>
        ) : null}

        <Field
          htmlFor="vehicle-document-file-input"
          label={t('contractors.personnelDocs.field.file')}
          error={fileError ?? undefined}
          hint={t('contractors.personnelDocs.field.fileHint')}
          required
        >
          <div className="flex items-center gap-3">
            <Button
              ref={chooseButtonRef}
              type="button"
              variant="secondary"
              size="sm"
              disabled={uploading}
              aria-invalid={fileError ? true : undefined}
              aria-describedby={`vehicle-document-file-name ${
                fileError ? 'vehicle-document-file-input-error' : 'vehicle-document-file-input-hint'
              }`}
              onClick={() => inputRef.current?.click()}
            >
              {t('vehicles.upload.chooseFile')}
            </Button>
            {file ? (
              <span
                id="vehicle-document-file-name"
                className="flex min-w-0 items-center gap-1.5 text-sm text-fg"
              >
                <Paperclip className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                <span className="truncate">{file.name}</span>
              </span>
            ) : (
              <span id="vehicle-document-file-name" className="text-sm text-muted">
                {t('contractors.personnelDocs.field.filePlaceholder')}
              </span>
            )}
          </div>
          <input
            ref={inputRef}
            id="vehicle-document-file-input"
            type="file"
            className="sr-only"
            tabIndex={-1}
            aria-hidden="true"
            accept={DOCUMENT_ACCEPT_ATTR}
            disabled={uploading}
            onChange={(e) => {
              const files = Array.from(e.target.files ?? []); // önce snapshot
              e.target.value = ''; // sonra reset (aynı dosya tekrar seçilebilsin)
              if (files.length) {
                pickFile(files);
              }
            }}
          />
        </Field>

        {progress !== null ? (
          <div className="flex flex-col gap-1" aria-live="polite">
            <progress
              className="sr-only"
              value={progress}
              max={100}
              aria-label={t('vehicles.upload.uploading')}
            />
            <div
              className="h-2 w-full overflow-hidden rounded-full bg-surface-muted"
              aria-hidden="true"
            >
              <div
                className="h-full bg-primary transition-[width] duration-150 motion-reduce:transition-none"
                style={{ width: `${progress}%` }}
              />
            </div>
            <span className="text-xs text-muted">
              {t('vehicles.upload.progress', { percent: progress })}
            </span>
          </div>
        ) : null}

        <DocumentTypeField
          id="vehicle-document-type"
          label={t('contractors.personnelDocs.field.type')}
          entity="vehicle"
          error={errors.type?.message}
          registration={docType.typeField}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            htmlFor="vehicle-document-valid-from"
            label={t('contractors.personnelDocs.field.validFrom')}
          >
            <Input id="vehicle-document-valid-from" type="date" {...register('validFrom')} />
          </Field>
          <ValidUntilField
            id="vehicle-document-valid-until"
            label={t('contractors.personnelDocs.field.validUntil')}
            rule={docType}
            error={errors.validUntil?.message}
            registration={register('validUntil')}
          />
        </div>

        <Field
          htmlFor="vehicle-document-note"
          label={t('contractors.personnelDocs.field.note')}
          error={errors.note?.message}
        >
          <Input
            id="vehicle-document-note"
            aria-invalid={errors.note ? true : undefined}
            aria-describedby={errors.note ? 'vehicle-document-note-error' : undefined}
            {...register('note')}
          />
        </Field>
      </form>
    </Modal>
  );
}
